import { cellAtWorld, cellCenter, heroWalkable } from '../grid.ts';
import { dist2, idiv, imax, imin, isqrt, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import { castSkill } from '../battle/skills.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { druidOrder, gladeUp, KNOT, NONE, nodeAt, onMound, thawWet } from '../battle/druid.ts';
import { rootsPlay, rootsSolve, type RootNet } from '../battle/druid-layout.ts';
import { useSkills } from './ghost.ts';

/**
 * The druid's chapter 1 played by the lab's bot (docs/SENDAS.md, «Calibración»; ai/senda-bot.ts sends it here):
 *
 * - the thaw (`thaw`): who knows reads the surge (where its wall is, when the next starts) and goes from mound to mound,
 *   on only while it can make the next before the wall gets to it, waiting on a mound while it passes, and fights from
 *   there; who doesn't goes straight up the way, fighting what comes at it.
 * - Olmo's clearing (`glade`): who knows keeps by Hob and strikes first a slime eating at a shoot, then what is at Hob
 *   or Ceda, then the slimes on their way to a shoot (the nearest to it first); who doesn't keeps by Ceda (the objective's
 *   first half) and fights what comes near.
 * - the root bridge (`roots`, the test): who knows works the sowing out with the rule (the solver, from the roots as they
 *   are: the one that arrives first has to leave the shared stone before the other sees it), strikes the Lodazal while
 *   it bubbles at a sprout, cuts a knot, steps onto a mound when the mud is coming and out of the slap; who doesn't sows
 *   every stone at once (the obvious), cuts the knot and sows them all again until the seeds run out.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

const guard = (s: BattleState, h: Hero, x: number, y: number, r: number): boolean => {
  const t = nearestTo(
    enemiesNear(s, x, y, r, (e) => targetable(s, e)),
    x,
    y,
  );
  if (!t) return false;
  strike(s, h, t);
  return true;
};

const npc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/* ---------- the thaw ---------- */

/** Ticks until the surge's wall reaches a row (y), from now (0: it is there; large: none coming). */
const surgeIn = (s: BattleState, y: number): number => {
  const r = s.rules?.senda?.druid?.thaw;
  const w = s.senda?.druid;
  if (!r || !w) return 9999;
  if (w.front !== NONE) {
    if (y <= w.front && y >= w.front - r.surge.len) return 0;
    if (y > w.front) return idiv(y - w.front, r.surge.speed);
  }
  const start = imax(0, w.next - s.waveT);
  return start + idiv(imax(0, y - r.top), r.surge.speed);
};

const thawBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.druid?.thaw;
  const w = s.senda?.druid;
  if (!r || !w) return;
  const pace = imax(1, idiv(h.stats.speed, 12));
  if (!knows) {
    if (guard(s, h, h.x, h.y, 2400)) return;
    h.target = 0;
    return go(s, h, r.glade.x, r.glade.y);
  }
  // The next mound up the slope (or the clearing), and whether it can be made before the wall.
  let ahead: { x: number; y: number } = r.glade;
  for (const m of r.mounds) if (m.y < h.y - 500 && (ahead === r.glade || m.y > ahead.y)) ahead = m;
  const d = isqrt(dist2(h.x, h.y, ahead.x, ahead.y));
  const need = idiv(idiv(d * 10, 6), pace) + 14;
  const on = onMound(s, h.x, h.y);
  const wallAhead = imin(surgeIn(s, ahead.y), surgeIn(s, h.y));
  // The flood below too near: on, whatever comes.
  const low = w.low !== NONE && w.low - h.y < 2600;
  if (on && !low && wallAhead < need) {
    // Wait it out where it stands, fighting from the mound.
    h.target = 0;
    if (guard(s, h, h.x, h.y, 3800)) {
      h.forced = false;
      orderMove(s, h, h.x, h.y);
    }
    return;
  }
  if (!on && !low && wallAhead < need) {
    // Back to the nearest mound (any).
    let best: { x: number; y: number } | null = null;
    let bd = 0x7fffffff;
    for (const m of r.mounds) {
      const dd = dist2(m.x, m.y, h.x, h.y);
      if (dd < bd) {
        bd = dd;
        best = m;
      }
    }
    if (best && idiv(isqrt(bd), pace) < surgeIn(s, best.y) + 30) {
      h.target = 0;
      return go(s, h, best.x, best.y, 150);
    }
  }
  if (thawWet(s, h.x, h.y) && guard(s, h, h.x, h.y, 1200)) return;
  if (guard(s, h, h.x, h.y, 2200)) return;
  h.target = 0;
  go(s, h, ahead.x, ahead.y);
};

/* ---------- Olmo's clearing ---------- */

const gladeBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.druid?.glade;
  const w = s.senda?.druid;
  if (!r || !w) return;
  const hob = npc(s, 'hob');
  const ceda = npc(s, 'ceda');
  if (!knows) {
    // (Who doesn't know the shoots matter keeps by Ceda, as the objective's first half says, and fights what comes.)
    const at = ceda ?? { x: 0, y: 0 };
    if (guard(s, h, at.x, at.y + 900, 3200)) return;
    h.target = 0;
    return go(s, h, at.x + 700, at.y + 900);
  }
  // The slimes on their way to a shoot: the one nearest its shoot first.
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.kind !== 'slime' || !targetable(s, e)) continue;
    for (let i = 0; i < gladeUp(r, s.waveT); i++) {
      if ((w.fenced | w.eaten) & (1 << i)) continue;
      const p = r.shoots[i] as { x: number; y: number };
      const dd = dist2(e.x, e.y, p.x, p.y);
      if (dd < bd) {
        bd = dd;
        best = e;
      }
    }
  }
  // (A slime at its shoot, eating, first: the shoot is lost in seconds; then what is at Hob or Ceda.)
  if (best && bd < 700 * 700) return strike(s, h, best);
  for (const p of [hob, ceda]) if (p && p.hp > 0 && guard(s, h, p.x, p.y, 1500)) return;
  if (best && bd < 5000 * 5000) return strike(s, h, best);
  for (const p of [hob, ceda]) if (p && p.hp > 0 && guard(s, h, p.x, p.y, 2600)) return;
  if (guard(s, h, h.x, h.y, 3000)) return;
  h.target = 0;
  if (hob) go(s, h, hob.x + 900, hob.y + 600);
};

/* ---------- the root bridge ---------- */

/** The knowing bot's plan: for the roots as they were, the sowings (node, tick) still to do. */
const PLAN = new WeakMap<BattleState, { key: string; sows: { n: number; t: number }[] }>();

const rootsKey = (s: BattleState): string => {
  const w = s.senda?.druid;
  const st = w?.roots;
  if (!w || !st) return '';
  return `${st.sprout}|${st.knot}|${w.seeds}|${st.tips.map((t) => `${t.at},${t.mode},${t.to},${t.follow}`).join(';')}`;
};

const plan = (s: BattleState): { n: number; t: number }[] => {
  const r = s.rules?.senda?.druid?.roots;
  const w = s.senda?.druid;
  if (!r || !w?.roots) return [];
  const key = rootsKey(s);
  const had = PLAN.get(s);
  if (had && had.key === key) return had.sows;
  const net: RootNet = { nodes: r.nodes, edge: r.edge };
  const P = { per: r.per, see: r.see, near: r.near };
  // (From a beat ahead: the hero still has to get there and throw.)
  const t0 = s.tick + r.throw + 18;
  const wins = rootsSolve(net, P, imin(3, w.seeds), 6, { from: w.roots, t0 });
  // The roomiest: the one that still wins with its throws early or late (a person is never exactly on time); then the
  // fewest seeds, then the quickest.
  const H = s.tick + P.per * 40;
  const room = (sows: { n: number; t: number }[]): number => {
    let ok = 0;
    for (let k = 0; k < sows.length; k++)
      for (const d of [-24, 24, 48]) if (rootsPlay(net, P, sows.map((x, j) => (j === k ? { n: x.n, t: x.t + d } : x)), H, w.roots).won) ok++;
    if (rootsPlay(net, P, sows.map((x) => ({ n: x.n, t: x.t + 24 })), H, w.roots).won) ok++;
    return idiv(ok * 100, sows.length * 3 + 1);
  };
  const scored = wins.slice(0, 400).map((x) => ({ x, k: room(x.sows) }));
  scored.sort((a, b) => b.k - a.k || a.x.sows.length - b.x.sows.length || a.x.t - b.x.t);
  const pick = scored[0]?.x;
  const sows = pick ? pick.sows : [];
  PLAN.set(s, { key, sows });
  return sows;
};

const rootsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.druid?.roots;
  const w = s.senda?.druid;
  if (!r || !w?.roots || w.done >= 0) return;
  const st = w.roots;
  // Out of the slap, onto a mound before the mud (who knows).
  if (knows && w.bmode === 5 && withinRange(h.x, h.y, w.bx, w.by, r.lodazal.slapR + 400)) {
    w.job = -1;
    const dx = h.x - w.bx;
    const dy = h.y - w.by;
    const d = imax(1, isqrt(dx * dx + dy * dy));
    const x = h.x + idiv(dx * 2600, d);
    const y = h.y + idiv(dy * 2600, d) + 600;
    h.target = 0;
    return go(s, h, heroWalkable(s, cellAtWorld(s, x, y)) ? x : h.x, heroWalkable(s, cellAtWorld(s, x, y)) ? y : h.y + 2000, 150);
  }
  if (knows && w.wave >= 0 && !onMound(s, h.x, h.y)) {
    w.job = -1;
    let best = -1;
    let bd = 0x7fffffff;
    const pts = r.mound.map((c) => cellCenter(s.map.radius, c));
    pts.forEach((p, k) => {
      const d = dist2(p.x, p.y, h.x, h.y);
      if (d < bd) {
        bd = d;
        best = k;
      }
    });
    const p = pts[best];
    if (p) {
      h.target = 0;
      return go(s, h, p.x, p.y, 120);
    }
  }
  // Mid-throw or mid-cut: let it be.
  if (w.job !== -1 && w.held > 0) return;
  const boss = w.boss ? findEnemy(s, w.boss) : null;
  // The bridge braiding: the Lodazal up on the bank (who knows strikes it; the slimes on the hero first).
  if (w.weave >= 0) {
    if (guard(s, h, h.x, h.y, 2600)) return;
    if (knows && boss && boss.hp > 0 && w.bmode !== 0) return strike(s, h, boss);
    if (guard(s, h, h.x, h.y, 4000)) return;
    return;
  }
  // The Lodazal at a sprout: strike it (who knows).
  if (knows && boss && boss.hp > 0 && (w.bmode === 1 || w.bmode === 2)) {
    w.job = -1;
    return strike(s, h, boss);
  }
  // A knot: cut it.
  if (st.knot >= 0) {
    if (guard(s, h, h.x, h.y, 900)) return;
    if (w.job !== -2) druidOrder(s, h, KNOT);
    return;
  }
  if (knows) {
    // A throw on its way: no new plan until it lands.
    if (w.job >= 3) return;
    const sows = plan(s);
    const due = sows.find((x) => x.t <= s.tick && !(st.sprout & (1 << x.n)));
    if (due && w.seeds > 0) {
      if (w.job !== due.n) druidOrder(s, h, due.n);
      return;
    }
    if (!sows.length && w.seeds > 0 && !st.tips.some((t) => t.mode === 1)) {
      // Nothing to plan from here: any stone not sprouted that a root sees.
      for (let n = 3; n < r.nodes.length; n++) {
        if (st.sprout & (1 << n)) continue;
        if (st.tips.some((t) => t.mode === 0 && (r.edge[t.at] as number[])[n])) {
          druidOrder(s, h, n);
          return;
        }
      }
    }
  } else if (w.seeds > 0) {
    // The obvious: every stone at once, one throw after another.
    for (let n = 3; n < r.nodes.length; n++) {
      if (st.sprout & (1 << n) || st.tips.every((t) => t.trail.includes(n))) continue;
      if (w.job !== n) druidOrder(s, h, n);
      return;
    }
  }
  if (w.job !== -1) return;
  if (guard(s, h, h.x, h.y, 3200)) return;
  h.target = 0;
  // By the middle of the bank, near the water.
  const x = idiv(nodeAt(r, 0).x + nodeAt(r, 1).x, 2);
  go(s, h, x, (r.bankRow + 1) * 866);
};

export const druidBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const d = s.rules?.senda?.druid;
  if (!d) return;
  if (d.thaw) return thawBot(s, h, knows);
  if (d.glade) return gladeBot(s, h, knows);
  if (d.roots) return rootsBot(s, h, knows);
};
